import { useRef, useEffect, useCallback } from 'react';
import ApexCharts from 'apexcharts';

declare global {
  interface Window {
    __apexcharts?: Record<string, ApexCharts>;
  }
}

declare global {
  interface Window {
    __reregisterAllCharts?: () => void;
  }
}

if (typeof window !== 'undefined') {
  window.__apexcharts = window.__apexcharts || {};

  window.__reregisterAllCharts = () => {
    const allCanvases = document.querySelectorAll('.apexcharts-canvas');
    allCanvases.forEach((canvas) => {
      const id = canvas.getAttribute('id');
      if (id && !window.__apexcharts?.[id]) {
        const container = canvas.closest('[class*="apexcharts"]') || canvas.parentElement;
        if (container) {
          const reactKey = Object.keys(container).find(
            (key) => key.startsWith('__reactInternalInstance') || key.startsWith('__reactFiber')
          );
          if (reactKey) {
            type ReactFiber = {
              memoizedProps?: { chart?: ApexCharts };
              stateNode?: { chart?: ApexCharts };
              return?: ReactFiber;
              _debugOwner?: ReactFiber;
            };
            const containerWithFiber = container as unknown as Record<string, ReactFiber>;
            let fiber: ReactFiber | undefined = containerWithFiber[reactKey];
            while (fiber) {
              if (fiber.memoizedProps?.chart || fiber.stateNode?.chart) {
                const instance = fiber.memoizedProps?.chart || fiber.stateNode?.chart;
                if (instance && typeof instance.dataURI === 'function') {
                  window.__apexcharts = window.__apexcharts || {};
                  window.__apexcharts[id] = instance;
                  break;
                }
              }
              fiber = fiber.return || fiber._debugOwner;
            }
          }
        }
      }
    });
  };
}

import Chart, { Props as ChartProps } from 'react-apexcharts';
import { Select, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { valueType } from 'antd/es/statistic/utils';
import { ApexOptions } from 'apexcharts';

export enum CHART_TYPES {
  LINE = 'line',
  AREA = 'area',
  BAR = 'bar',
  PIE = 'pie',
  DONUT = 'donut',
  RADIAL_BAR = 'radialBar',
  SCATTER = 'scatter',
  BUBBLE = 'bubble',
  HEATMAP = 'heatmap',
  CANDLESTICK = 'candlestick',
  BOX_PLOT = 'boxPlot',
  RADAR = 'radar',
  POLAR_AREA = 'polarArea',
  RANGE_BAR = 'rangeBar',
  RANGE_AREA = 'rangeArea',
  TREEMAP = 'treemap',
}

type DefaultOptionType = {
  label: string;
  value: valueType;
};

interface IChartComponent {
  title: string;
  series: ApexOptions['series'];
  options: ApexCharts.ApexOptions;
  type: CHART_TYPES;
  sideFilters?: DefaultOptionType[];
  handleSideFiltersChange?: (filter: valueType) => void;
  exportBtnAction?: () => void;
  height?: number;
  maxHeight?: number;
  width?: number;
  loading?: boolean;
  topWidgets?: JSX.Element;
  selectedSideFilter?: any;
}

const ChartComponent = (props: IChartComponent) => {
  const {
    title,
    series,
    options,
    type,
    height = 800,
    maxHeight = 800,
    sideFilters,
    handleSideFiltersChange,
    loading,
    topWidgets,
    selectedSideFilter
  } = props;

  const chartRef = useRef<ChartProps['ref']>(null);
  const { t } = useTranslation(['common']);

  const dynamicHeight = height > maxHeight ? maxHeight : height;

  const registerChartInstance = useCallback((instance: ApexCharts) => {
    const instanceInternal = instance as ApexCharts & {
      w?: { gl?: { dom?: { BaseEl?: HTMLElement } } };
      el?: HTMLElement;
      chartID?: string;
    };

    let canvasEl: Element | null = null;

    if (instanceInternal?.w?.gl?.dom?.BaseEl) {
      canvasEl = instanceInternal.w.gl.dom.BaseEl.querySelector('.apexcharts-canvas');
    }

    if (!canvasEl && instanceInternal?.el) {
      const container = instanceInternal.el;
      canvasEl = container.querySelector?.('.apexcharts-canvas') || null;
    }

    if (!canvasEl && instanceInternal?.chartID) {
      canvasEl = document.querySelector(`#${instanceInternal.chartID}`);
    }

    if (!canvasEl && instanceInternal?.el?.parentElement) {
      canvasEl = instanceInternal.el.parentElement.querySelector('.apexcharts-canvas');
    }

    if (!canvasEl && chartRef.current) {
      const refWithContainer = chartRef.current as {
        container?: HTMLElement;
        el?: { parentElement?: HTMLElement };
      } | null;
      const container = refWithContainer?.container || refWithContainer?.el?.parentElement;
      if (container) {
        canvasEl = container.querySelector?.('.apexcharts-canvas') || null;
      }
    }

    const canvasId = canvasEl?.getAttribute('id');

    if (canvasId && instance) {
      window.__apexcharts = window.__apexcharts || {};
      window.__apexcharts[canvasId] = instance;
    } else {
      setTimeout(() => {
        const allCanvases = document.querySelectorAll('.apexcharts-canvas');
        for (const canvas of allCanvases) {
          const id = canvas.getAttribute('id');
          if (id && !window.__apexcharts?.[id]) {
            const refWithContainer = chartRef.current as {
              container?: HTMLElement;
              el?: HTMLElement;
            } | null;
            const chartContainer = refWithContainer?.container || refWithContainer?.el;
            if (chartContainer && chartContainer.contains(canvas)) {
              window.__apexcharts = window.__apexcharts || {};
              window.__apexcharts[id] = instance;
              break;
            }
          }
        }
      }, 500);
    }
  }, []);

  useEffect(() => {
    if (loading) return;

    const attemptRegistration = (retries = 0, maxRetries = 10) => {
      const chartWrapper = chartRef.current as { chart?: ApexCharts } | null;
      if (chartWrapper?.chart) {
        registerChartInstance(chartWrapper.chart);
        return;
      }

      if (retries < maxRetries) {
        const delay = Math.min(100 * Math.pow(1.5, retries), 2000);
        setTimeout(() => attemptRegistration(retries + 1, maxRetries), delay);
      }
    };

    attemptRegistration();
  }, [loading, series, options, registerChartInstance]);

  useEffect(() => {
    if (loading) return;

    const containerElement = document.querySelector('.py-4');
    if (!containerElement) return;

    const observer = new MutationObserver(() => {
      const chartWrapper = chartRef.current as { chart?: ApexCharts } | null;
      if (chartWrapper?.chart) {
        const instance = chartWrapper.chart;
        const instanceInternal = instance as ApexCharts & {
          w?: { gl?: { dom?: { BaseEl?: HTMLElement } } };
          el?: HTMLElement;
          chartID?: string;
        };

        if (instanceInternal?.w?.gl?.dom?.BaseEl) {
          const canvasEl = instanceInternal.w.gl.dom.BaseEl.querySelector('.apexcharts-canvas');
          const canvasId = canvasEl?.getAttribute('id');
          if (canvasId) {
            window.__apexcharts = window.__apexcharts || {};
            if (!window.__apexcharts[canvasId]) {
              window.__apexcharts[canvasId] = instance;
            }
          }
        }
      }
    });

    observer.observe(containerElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['id', 'class'],
    });

    return () => observer.disconnect();
  }, [loading]);

  return (
    <div className="w-full">
      <div className="flex flex-col justify-between lg:flex-row">
        <div className="flex flex-col items-start gap-4 md:flex-row md:items-center">
          <h3 className="text-2xl font-semibold">{title}</h3>
        </div>

        <div className="mt-4 lg:mt-0">
          {sideFilters && handleSideFiltersChange && (
            <Select
              popupClassName="!w-[150px]"
              className="w-[max(fit-content,120px)] [&>.ant-select-selector]:!border-none [&>.ant-select-selector]:!pl-0 [&_.ant-select-selection-item]:!text-base [&_.ant-select-selection-item]:!text-black [&_.ant-select-selection-placeholder]:!text-base [&_.ant-select-selection-placeholder]:!text-black [&_.ant-select-selection-placeholder]:hover:!text-black/50 [&_input]:!capitalize"
              options={sideFilters.map((filter) => ({
                label: filter.label,
                value: filter.value,
              }))}
              defaultValue={sideFilters[0].value}
              placeholder={t('select')}
              onChange={(value) => handleSideFiltersChange(value)}
              value={selectedSideFilter}
            />
          )}
        </div>
      </div>

      {topWidgets && topWidgets}

      {loading && (
        <div
          className="flex w-full flex-row items-center justify-center"
          style={{ height: dynamicHeight || 600 }}
        >
          <Spin size="large" />
        </div>
      )}

      {!loading && (
        <div className="py-4">
          <Chart
            ref={chartRef}
            options={options}
            series={series}
            type={type}
            height={dynamicHeight}
          />
        </div>
      )}
    </div>
  );
};

export default ChartComponent;
